import React, { useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart,
  Legend,
  Cell,
} from "recharts";
import { format, parseISO } from "date-fns";
import {
  BarChart2,
  SortDesc,
  ArrowDownRight,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "../ui/button";

interface ChartProps {
  data: any[];
  type: "line" | "bar";
}

const BehaviorInsightsChart = ({ data, type }: ChartProps) => {
  const [sortBy, setSortBy] = useState<"value" | "alphabetical">("value");

  // Prepare data for display, ensuring dates are sorted chronologically for line charts
  // or values are sorted for bar charts
  const prepareChartData = () => {
    if (data.length === 0) {
      return [{ name: "No data available", value: 0 }];
    }

    if (type === "line" && "date" in data[0]) {
      // Sort the data by date in ascending order
      return [...data].sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateA - dateB;
      });
    }

    // For bar charts, sort by value or alphabetically
    if (type === "bar") {
      const sortedData = [...data];

      if (sortBy === "value") {
        return sortedData.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
      } else {
        return sortedData.sort((a, b) => a.name.localeCompare(b.name));
      }
    }

    return data;
  };

  const chartData = prepareChartData();

  // Get the maximum absolute value for setting chart domain
  const getMaxValue = () => {
    if (data.length === 0) return 1000;

    const maxAbsValue = Math.max(
      ...data.map((item) =>
        Math.abs(typeof item.value === "number" ? item.value : 0)
      )
    );

    // Round up to nearest thousand for cleaner axis
    return Math.ceil(maxAbsValue / 1000) * 1000;
  };

  // Custom formatter for dates
  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), "MMM dd");
    } catch (e) {
      return dateStr;
    }
  };

  // Format currency values
  const formatCurrency = (value: number) => {
    return `₹${Math.abs(value).toLocaleString("en-IN")}`;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataValue = payload[0].value;
      const isPositive = dataValue >= 0;

      return (
        <div className="bg-black/90 p-3 border border-market-teal/30 rounded-lg shadow-xl backdrop-blur-sm">
          <p className="text-white text-sm font-medium mb-1">
            {payload[0].payload.date
              ? formatDate(payload[0].payload.date)
              : label}
          </p>
          <div className="flex items-center gap-2">
            {isPositive ? (
              <ArrowUpRight className="h-4 w-4 text-green-400" />
            ) : (
              <ArrowDownRight className="h-4 w-4 text-red-400" />
            )}
            <p
              className={`text-sm font-semibold ${
                isPositive ? "text-green-400" : "text-red-400"
              }`}
            >
              {typeof dataValue === "number"
                ? formatCurrency(dataValue)
                : dataValue}
            </p>
          </div>
          {payload[0].payload.tradeCount && (
            <p className="text-xs text-white/70 mt-1">
              Trades: {payload[0].payload.tradeCount}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // For bar chart colors
  const getBarColor = (value: number) => {
    return value >= 0 ? "#10b981" : "#ef4444";
  };

  return (
    <div className="h-[340px] w-full flex flex-col">
      {type === "bar" && (
        <div className="flex justify-end mb-2">
          <div className="flex items-center gap-1 bg-white/5 p-1 rounded-md">
            <Button
              variant={sortBy === "value" ? "default" : "outline"}
              size="sm"
              className={`h-7 px-2 py-1 text-xs ${
                sortBy === "value" ? "bg-white/10" : ""
              }`}
              onClick={() => setSortBy("value")}
            >
              <SortDesc className="h-3 w-3 mr-1" />
              Value
            </Button>
            <Button
              variant={sortBy === "alphabetical" ? "default" : "outline"}
              size="sm"
              className={`h-7 px-2 py-1 text-xs ${
                sortBy === "alphabetical" ? "bg-white/10" : ""
              }`}
              onClick={() => setSortBy("alphabetical")}
            >
              <BarChart2 className="h-3 w-3 mr-1" />
              A-Z
            </Button>
          </div>
        </div>
      )}

      <div className="flex-1">
        {data.length === 0 ? (
          <div className="h-full w-full flex items-center justify-center">
            <p className="text-white/60">Not enough data to display chart</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {type === "line" ? (
              <AreaChart
                data={chartData}
                margin={{ top: 10, right: 10, bottom: 25, left: 10 }}
              >
                <defs>
                  <linearGradient id="colorPnl" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.05)"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  stroke="rgba(255,255,255,0.3)"
                  tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 12 }}
                  tickFormatter={formatDate}
                  axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                  dy={10}
                />
                <YAxis
                  stroke="rgba(255,255,255,0.3)"
                  tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 12 }}
                  tickFormatter={(value) => `₹${value}`}
                  axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                  dx={-10}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ stroke: "rgba(255,255,255,0.2)" }}
                />
                <ReferenceLine
                  y={0}
                  stroke="rgba(255,255,255,0.2)"
                  strokeDasharray="3 3"
                />
                <Area
                  type="monotone"
                  dataKey="pnl"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  fill="url(#colorPnl)"
                  activeDot={{
                    r: 6,
                    stroke: "#10b981",
                    strokeWidth: 2,
                    fill: "#000",
                  }}
                  dot={{
                    r: 4,
                    strokeWidth: 2,
                    stroke: "#10b981",
                    fill: "#000",
                  }}
                />
              </AreaChart>
            ) : (
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 10, bottom: 10, left: 20 }}
                layout="vertical"
                barCategoryGap="30%" // Add more space between bars
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.05)"
                  horizontal={true}
                  vertical={false}
                />
                <XAxis
                  type="number"
                  stroke="rgba(255,255,255,0.3)"
                  tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 12 }}
                  axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                  tickFormatter={(value) => `₹${value}`}
                  domain={[-getMaxValue(), getMaxValue()]}
                />
                <YAxis
                  type="category"
                  width={150}
                  dataKey="name"
                  stroke="rgba(255,255,255,0.3)"
                  tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 12 }}
                  axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: "rgba(255,255,255,0.05)" }}
                />
                <ReferenceLine x={0} stroke="rgba(255,255,255,0.2)" />
                <Bar
                  dataKey="value"
                  radius={[0, 8, 8, 0]} // Rounded bars on the right side
                  barSize={20} // Consistent bar size
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getBarColor(entry.value)}
                    />
                  ))}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default BehaviorInsightsChart;
